import { getDefaultStore } from "jotai";
import { useEffect } from "react";
import socketIO from "socket.io-client";
import z from "zod";
import { authAtom } from "./auth.service";

export const lassoPushMessageSchema = z.object({
  type: z.string(),
  message: z.unknown(),
});

export type LassoPushMessage = z.infer<typeof lassoPushMessageSchema>;

export type LassoPushMessageListener = (pushMessage: LassoPushMessage) => void;

const socket = socketIO(
  import.meta.env.VITE_LASSO_PUSH_URL ?? "https://push.lassox.com",
  {
    transports: ["websocket", "polling"], // Try websocket first, then polling if needed
  },
);

const listenersByType = new Map<
  string,
  Map<symbol, LassoPushMessageListener>
>();

// Handle incoming push messages
socket.onAny((_, pushMessageJSON) => {
  if (typeof pushMessageJSON !== "string") return;
  const pushMessageRaw = JSON.parse(pushMessageJSON);

  const pushMessage = lassoPushMessageSchema.safeParse(pushMessageRaw).data;
  if (!pushMessage) return;

  const listeners = listenersByType.get(pushMessage.type);
  if (!listeners) return;

  for (const listener of listeners.values()) {
    listener(pushMessage);
  }
});

export function addPushMessageListener(
  type: string,
  listener: LassoPushMessageListener,
) {
  const symbol = Symbol();
  let listeners = listenersByType.get(type);
  if (!listeners) {
    listeners = new Map();
    listenersByType.set(type, listeners);
  }
  listeners.set(symbol, listener);
  const removePushMessageListener = () => {
    listeners.delete(symbol);
  };
  return removePushMessageListener;
}

export function emit(event: string, ...args: unknown[]) {
  socket.emit(event, ...args);
}

export function usePushMessageListener(
  type: string,
  listener: LassoPushMessageListener | null,
) {
  useEffect(() => {
    if (!listener) return;
    const unlisten = addPushMessageListener(type, listener);
    return () => {
      unlisten();
    };
  }, [type, listener]);
}

// Clean up socket connection on hot module replacement
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    import.meta.hot?.invalidate();
  });
}
// Handle joining and leaving rooms
const currentRooms = new Map<string, Room>();

type Room = {
  joined: boolean;
  connected: boolean;
  consumers: Map<
    symbol,
    {
      onConnected?: () => void;
      onDisconnected?: () => void;
    }
  >;
  join: () => void;
  leave: () => void;
};

export function joinRoom(
  roomId: string,
  options?: {
    onConnected?: () => void;
    onDisconnected?: () => void;
  },
) {
  const symbol = Symbol();

  const createRoom = () => {
    const room: Room = {
      joined: false,
      connected: false,
      consumers: new Map(),
      join: () => {},
      leave: () => {},
    };

    currentRooms.set(roomId, room);

    room.join = () => {
      if (room.joined) return;

      room.joined = true;

      const onConnected = () => {
        room.connected = true;
        emit("join", roomId);
        for (const consumer of room.consumers.values()) {
          consumer.onConnected?.();
        }
      };

      const onDisconnected = () => {
        room.connected = false;
        for (const consumer of room.consumers.values()) {
          consumer.onDisconnected?.();
        }
      };

      if (socket.connected) onConnected();

      socket.on("connect", onConnected);
      socket.on("disconnect", onDisconnected);

      room.leave = () => {
        if (!room.joined) return;

        room.joined = false;
        room.leave = () => {};

        socket.off("connect", onConnected);
        socket.off("disconnect", onDisconnected);

        if (socket.connected) {
          socket.emit("leave", roomId);
        }
      };
    };

    return room;
  };

  const room = currentRooms.get(roomId) || createRoom();

  room.consumers.set(symbol, {
    onConnected: options?.onConnected,
    onDisconnected: options?.onDisconnected,
  });

  if (room.joined) {
    if (room.connected) options?.onConnected?.();
  } else {
    room.join();
  }

  return () => {
    if (room.consumers.delete(symbol) && room.consumers.size === 0) {
      room.leave();
      currentRooms.delete(roomId);
    }
  };
}

// Connect to correct room whenever auth changes
{
  let currentOrgRoom: {
    room: string;
    leave: () => void;
  } | null = null;

  const jotaiStore = getDefaultStore();

  jotaiStore.sub(authAtom, () => {
    const auth = jotaiStore.get(authAtom).data;

    const userOrgId = auth?.user.lassoOrganizationId ?? null;

    if (currentOrgRoom?.room === userOrgId) return;

    currentOrgRoom?.leave();

    if (userOrgId) {
      const leave = joinRoom(userOrgId);
      currentOrgRoom = {
        room: userOrgId,
        leave,
      };
    } else {
      currentOrgRoom = null;
    }
  });
}
